import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useParams, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { Route } from '@modernhealth/web-analytics';
import { Icon, BodyTextSmall, pxToRem } from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';

import { navigationService } from 'src/config/rootService';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import {
    useConversation,
    ConversationState,
    RequestStateType,
} from 'src/shared/messaging-kit/hooks/useConversation';
import { CreateMessage } from '../components/CreateMessage';
import { ProviderDetailsModal } from 'src/modules/providers/components/ProviderDetailsModal';
import { ChangeProviderModal } from 'src/modules/care/components/ChangeProviderModal';
import { RemoveProviderModal } from 'src/modules/care/components/RemoveProviderModal';

import { ConversationHeader } from '../components/ConversationHeader';
import { MessageSpinner } from '../components/MessageLoadingSpinner';
import { MessagesList } from '../components/MessagesList';
import { InlineButtonLink } from '../components/InlineButtonLink';
import { ConversationNotFound } from '../components/ConversationNotFound';
import { StartAConversation } from '../components/StartAConversation';
import { ProviderResponseInfo } from '../components/ProviderResponseInfo';

export interface ConversationSubRouteParams {
    uuid: string;
}

export const ConversationSubRoute: React.FC<{
    goToNextConversation: () => void;
    setShowMessagesPageElements: (boolean) => void;
}> = ({ goToNextConversation, setShowMessagesPageElements }) => {
    const { uuid } = useParams<ConversationSubRouteParams>();
    const { url } = useRouteMatch();
    const user = useSelector(getUserInformation);
    const {
        conversation,
        messages,
        requestMoreMessages,
        messagesRequestState,
        sendMessage,
        sendMessageRequestState,
        sendMessageReadReceipt,
        conversationState,
    } = useConversation(uuid);
    const { t } = useTranslation();

    const containerRef = React.useRef<HTMLDivElement>(null);

    // Send read receipts for all messages in view that are intended for our user.
    useEffect(() => {
        messages.forEach(
            m => !m.readAt && m.receiverUuid === user.uuid && sendMessageReadReceipt(m),
        );
    }, [messages, user, sendMessageReadReceipt]);

    const disableLoadMore = [
        ConversationState.LOADING,
        ConversationState.LOADING_MORE_MESSAGES,
        ConversationState.END_OF_CONVERSATION,
        ConversationState.NO_MESSAGES,
        ConversationState.ERROR,
    ].includes(conversationState);

    const handleScroll = () => {
        if (!!containerRef.current) {
            const { scrollHeight, scrollTop, clientHeight } = containerRef.current;
            const atTop = scrollHeight + scrollTop === clientHeight;

            if (atTop && messagesRequestState.state === RequestStateType.IDLE && !disableLoadMore) {
                requestMoreMessages();
            }
        }
    };

    useEffect(() => {
        if (!disableLoadMore && messagesRequestState.state === RequestStateType.IDLE) {
            if (
                containerRef.current &&
                containerRef.current.scrollHeight - containerRef.current.clientHeight <= 0
            ) {
                requestMoreMessages();
            }
        }
    }, [disableLoadMore, messagesRequestState, requestMoreMessages]);

    useEffect(() => {
        if (conversationState === ConversationState.NOT_FOUND) {
            setShowMessagesPageElements(false);
        }
    }, [conversationState, setShowMessagesPageElements]);

    useEffect(() => {
        setShowMessagesPageElements(true);
    }, [uuid, setShowMessagesPageElements]);

    if (conversationState === ConversationState.NOT_FOUND) {
        return <ConversationNotFound />;
    }

    return (
        <>
            <StyledConversationsContainer>
                {!!conversation && <ConversationHeader conversation={conversation} />}
                <MessageSpinner conversationState={conversationState} />

                <StyledMessageContainer ref={containerRef} onScroll={handleScroll}>
                    {conversationState === ConversationState.NO_MESSAGES ? (
                        <StartAConversation coach={conversation?.coach} />
                    ) : (
                        <MessagesList conversation={conversation} messages={messages} />
                    )}
                    {/*
                     * To be replaced by the InlineCallout component from Vita
                     * once the issues are sorted out with icon alignment.
                     */}
                    {messagesRequestState.state === RequestStateType.ERROR && (
                        <StyledErrorBox>
                            <StyledErrorBoxIcon name="alert-enclosed-filled" color="red65" />
                            <BodyTextSmall>
                                <Trans
                                    t={t}
                                    i18nKey="messaging:conversation.error.unable_to_load_previous_messages"
                                    components={{
                                        'try-again-link': (
                                            <InlineButtonLink
                                                inheritColor
                                                onClick={() => requestMoreMessages()}
                                            />
                                        ),
                                    }}
                                />
                            </BodyTextSmall>
                        </StyledErrorBox>
                    )}
                </StyledMessageContainer>
                <ProviderResponseInfo messages={messages} conversation={conversation} />
                {!!conversation && (
                    <CreateMessage
                        conversationUuid={conversation.uuid}
                        sendMessage={sendMessage}
                        sendMessageRequestState={sendMessageRequestState}
                    />
                )}
            </StyledConversationsContainer>
            {!!conversation?.coach && (
                <>
                    <Route
                        path={`${url}/profile`}
                        analytics={{ pageName: 'chat-conversation-provider-profile' }}
                        exact
                    >
                        <ProviderDetailsModal
                            isVisible
                            providerUuid={conversation.coach.uuid}
                            onClose={() => navigationService.navigate(url)}
                        />
                    </Route>
                    <Route
                        path={`${url}/change`}
                        analytics={{ pageName: 'chat-conversation-change-provider' }}
                        exact
                    >
                        <ChangeProviderModal
                            isVisible
                            providerUuid={conversation.coach.uuid}
                            onCancel={() => navigationService.navigate(url)}
                        />
                    </Route>
                    <Route
                        path={`${url}/remove`}
                        analytics={{ pageName: 'chat-conversation-remove-provider' }}
                        exact
                    >
                        <RemoveProviderModal
                            isVisible
                            providerUuid={conversation.coach.uuid}
                            onConfirm={goToNextConversation}
                            onCancel={() => navigationService.navigate(url)}
                        />
                    </Route>
                </>
            )}
        </>
    );
};

const StyledMessageContainer = styled.div({
    display: 'flex',
    flexDirection: 'column-reverse',
    height: 0,
    overflow: 'auto',
    flexGrow: 1,
    marginBottom: pxToRem(15),
    paddingRight: pxToRem(30),
});

const StyledConversationsContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
});

const StyledErrorBox = styled.div`
    padding: ${pxToRem(16)};
    background-color: ${colors.red10};
    color: ${colors.red85};
    margin: ${pxToRem(16, 0)};
    display: flex;
    align-items: center;
`;

const StyledErrorBoxIcon = styled(Icon)`
    margin-right: ${pxToRem(10)};
`;
